import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FavoritesItem } from './FavoritesItem.tsx';
import { type FavoritesProviderChildrenProps } from './FavoritesProviderChildrenProps.ts';
import {
    isCompactTileSize,
    type TileSize,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';

import { type SharedFavoriteItem_item$key } from './__generated__/SharedFavoriteItem_item.graphql.ts';

import styles from './SharedFavoriteItem.scss';

const itemFragment = graphql`
    fragment SharedFavoriteItem_item on Item {
        isNewListing
        ...FavoritesItem_item
    }
`;

type Props = {
    item: SharedFavoriteItem_item$key | null | undefined;
    isSponsored?: boolean;
    favorites?: FavoritesProviderChildrenProps;
    fetchFolderOnMouseOver?: boolean;
    showFavoritesTooltip?: boolean;
    showNudge?: boolean;
    tileSize: TileSize;
    wrapperClass?: string;
    isFolderVisible: boolean;
    showFolderPlusIcon?: boolean;
    onUnFavorited?: () => void;
    onFolderActionStart?: () => void;
    onFolderActionEnd?: () => void;
};

export const SharedFavoriteItem: FC<Props> = ({
    item: itemRef,
    isFolderVisible,
    isSponsored,
    favorites,
    fetchFolderOnMouseOver,
    showFavoritesTooltip,
    showNudge,
    tileSize,
    wrapperClass = '',
    showFolderPlusIcon,
    onUnFavorited,
    onFolderActionStart,
    onFolderActionEnd,
}) => {
    const item = useFragment(itemFragment, itemRef);
    if (!favorites) {
        return null;
    }
    const theme = isCompactTileSize(tileSize) ? 'circle-frosted-small' : 'circle-frosted-medium';
    return (
        <FavoritesItem
            isNewListing={item?.isNewListing || false}
            isSponsored={isSponsored}
            wrapperClass={classnames(styles.favoritesWrapper, wrapperClass)}
            heartIconWrapperClass={styles.heartIconWrapperSpacing}
            folderIconWrapperClass={styles.folderWrapper}
            item={item}
            {...favorites}
            fetchFolder={favorites.fetchFolder && (!fetchFolderOnMouseOver || !!isFolderVisible)}
            showTooltip={showFavoritesTooltip}
            showNudge={showNudge}
            theme={theme}
            showFolderPlusIcon={showFolderPlusIcon}
            onUnFavorited={onUnFavorited}
            onFolderActionStart={onFolderActionStart}
            onFolderActionEnd={onFolderActionEnd}
        />
    );
};
